import { useEffect } from "react";
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  useRouter,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import NotFound from "@/pages/NotFound";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import appCss from "../styles.css?url";
import { ADS_SCRIPT_URL, loadExternalScript } from "@/lib/third-party";

// WebSite JSON-LD (ported from index.html; potentialAction removed — the
// /resultados/{term} search action does not exist as a search feature).
const websiteJsonLd = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "TiemposHoy",
  alternateName: ["Tiempos Hoy", "TiemposHoy.com"],
  url: "https://tiemposhoy.com",
  description:
    "Resultados actualizados de la Diaria Loto de Nicaragua y Tiempos JPS de Costa Rica. Consulta los números ganadores de hoy.",
  inLanguage: "es",
  publisher: {
    "@type": "Organization",
    name: "TiemposHoy.com",
    url: "https://tiemposhoy.com",
    logo: {
      "@type": "ImageObject",
      url: "https://tiemposhoy.com/favicon.svg",
    },
  },
});

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      {
        name: "robots",
        content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
      },
      {
        title: "Tiempos Hoy - Resultados de Nicaragua y Costa Rica | Diaria Loto y JPS",
      },
      {
        name: "description",
        content:
          "Consulta los resultados de hoy de la Diaria Loto de Nicaragua y los Tiempos JPS de Costa Rica. Resultados actualizados de las 11am, 3pm y 9pm. ¡Revisa si ganaste!",
      },
      {
        name: "keywords",
        content:
          "tiempos hoy, nica hoy, tica hoy, nuevos tiempos, resultados lotería nicaragua, resultados lotería costa rica, diaria loto, tiempos jps",
      },
      { name: "author", content: "TiemposHoy.com" },
      { name: "language", content: "Spanish" },
      { property: "og:title", content: "Tiempos Hoy - Resultados Nicaragua y Costa Rica" },
      {
        property: "og:description",
        content:
          "Resultados actualizados de la Diaria Loto (Nicaragua) y Tiempos JPS (Costa Rica). Consulta los números ganadores de hoy.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://tiemposhoy.com" },
      { property: "og:locale", content: "es_ES" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Tiempos Hoy - Resultados Nicaragua y Costa Rica" },
      {
        name: "twitter:description",
        content: "Resultados actualizados de la Diaria Loto y Tiempos JPS",
      },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.svg", type: "image/svg+xml" },
      { rel: "dns-prefetch", href: "https://jrnlppukczpjqoggcvcz.supabase.co" },
      { rel: "preconnect", href: "https://jrnlppukczpjqoggcvcz.supabase.co", crossOrigin: "anonymous" },
      {
        rel: "alternate",
        type: "text/plain",
        href: "/llms.txt",
        title: "LLMs.txt - AI readable site summary",
      },
    ],
    scripts: [
      { type: "application/ld+json", children: websiteJsonLd },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="es" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  useEffect(() => {
    // Queue tracking immediately, including events before the remote script loads.
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
      window.gtag = function () { window.dataLayer?.push(arguments); };
      window.gtag("js", new Date());
      window.gtag("config", "G-NS342JX9QJ");
    }
    const analytics = window.setTimeout(() => {
      loadExternalScript("https://www.googletagmanager.com/gtag/js?id=G-NS342JX9QJ");
    }, 1000);
    // AdSense remains in the head on all pages, but only after initial painting.
    const ads = window.setTimeout(() => loadExternalScript(ADS_SCRIPT_URL, "anonymous"), 3000);
    return () => {
      window.clearTimeout(analytics);
      window.clearTimeout(ads);
    };
  }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <Outlet />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  if (import.meta.env.DEV) console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
      <div className="text-center p-8 max-w-md">
        <h1 className="mb-3 text-2xl font-bold">Esta página no cargó</h1>
        <p className="mb-6 text-muted-foreground">
          Algo salió mal. Puedes intentar de nuevo o volver al inicio.
        </p>
        <div className="flex justify-center gap-3">
          <button
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground font-medium"
            onClick={() => {
              router.invalidate();
              reset();
            }}
          >
            Intentar de nuevo
          </button>
          <a
            className="px-4 py-2 rounded-md border border-border text-foreground font-medium"
            href="/"
          >
            Ir al inicio
          </a>
        </div>
      </div>
    </div>
  );
}
